import { useMemo } from "react";

/**
 * Derives the contextual chat-teaser message for the current route.
 * Returns `{ key, text }` — with `key === null` when the teaser should be
 * suppressed (legal pages, /admin, etc.).
 *
 * Extracted from ChatWidget.jsx so the widget component focuses on rendering
 * and networking, and the teaser routing table can grow independently.
 *
 * @param {object} location  react-router location ({pathname, hash, search})
 * @param {object} ct        translated chat block (t.chat) — reads contextual_teasers + teaser_msg
 * @returns {{key: string|null, text: string|null}}
 */
export default function useContextualTeaser(location, ct) {
  return useMemo(() => {
    const tt = (ct && ct.contextual_teasers) || {};
    const path = (location.pathname || "/").toLowerCase();
    const hash = (location.hash || "").toLowerCase();
    const search = (location.search || "").toLowerCase();

    if (path.startsWith("/impressum") || path.startsWith("/datenschutz") || path.startsWith("/agb")) {
      return { key: null, text: null };
    }
    // Admin dashboard — no marketing teaser for staff
    if (path.startsWith("/admin")) return { key: null, text: null };
    // Booking success page
    if (path.startsWith("/booking/success")) return { key: "booking_success", text: tt.booking_success };
    if (path.startsWith("/book")) {
      // Order matters: hausreinigung before reinigung (substring collision)
      if (search.includes("service=autoteile")) return { key: "autoteile", text: tt.autoteile };
      if (search.includes("service=umzug")) return { key: "umzug", text: tt.umzug };
      if (search.includes("service=hausreinigung")) return { key: "hausreinigung", text: tt.hausreinigung };
      if (search.includes("service=gartenpflege")) return { key: "gartenpflege", text: tt.gartenpflege };
      if (search.includes("service=plakatservice")) return { key: "plakatservice", text: tt.plakatservice };
      if (search.includes("service=reinigung")) return { key: "reinigung", text: tt.reinigung };
      return { key: "book", text: tt.book };
    }
    if (path.startsWith("/track")) return { key: "track", text: tt.track };
    if (path.startsWith("/dashboard")) return { key: "dashboard", text: tt.dashboard };
    if (path.startsWith("/blog")) return { key: "blog", text: tt.blog };
    if (path.startsWith("/login") || path.startsWith("/forgot-password") || path.startsWith("/reset-password")) {
      return { key: "login", text: tt.login };
    }
    // Homepage hash anchors — priority order matters
    if (hash.includes("preise") || hash.includes("calc")) return { key: "price", text: tt.price };
    if (hash.includes("services") || hash.includes("leistungen")) return { key: "services", text: tt.services };
    if (hash.includes("contact") || hash.includes("kontakt")) return { key: "contact", text: tt.contact };
    if (hash.includes("about") || hash.includes("ueber") || hash.includes("über")) return { key: "about", text: tt.about };
    return { key: "default", text: tt.default || (ct && ct.teaser_msg) };
  }, [location.pathname, location.hash, location.search, ct]);
}
